import { Effect } from 'effect'
import { v5 as uuidv5 } from 'uuid'
import type { NonEmptyArray } from 'effect/Array'
import { toInteger } from '@repo/money'
import { betStatusMap } from 'src/types/domain/BetStatus'
import type { BetStatus } from 'src/types/domain/BetStatus'
import type { Bet } from 'src/types/domain/Bet'
import type { RaceSettledEvent } from 'src/types/domain/RaceEvent'
import { displayMoney } from './displayAmount'
import { displayExactMoney } from './displayExactMoney'
import { containsSameItems } from './containsSameItems'

export const BetClassNameMap: Record<BetStatus, string> = {
  [betStatusMap.WON]: 'won',
  [betStatusMap.LOST]: 'lost',
  [betStatusMap.CANCELLED]: 'cancelled',
  [betStatusMap.PENDING]: 'pending',
  [betStatusMap.CASHED_OUT]: 'won' // Treat cashed_out as won for styling
}

export const getVisualPotentialPayout = (
  currentBet: Bet,
  currentBetStatus: BetStatus,
  // Crash only: a PENDING crash bet has no knowable payout. Its stored
  // potentialPayout is the operator LIABILITY CEILING (CrashPotentialPayout
  // ignores the stake), so rendering it under a "Won Amount" label told a player
  // mid-round they had won 250,000. CASHED_OUT is safe — BetMap.fromBetDTO
  // overwrites potentialPayout with the realized actualPayout on cash-out.
  isCrash = false,
  // Render the FULL stored amount instead of 2dp. Only the EXPANDED bet-history
  // row passes this: money is stored at 5-decimal wire precision but displayed at
  // 2 (truncated), so a 40.71978 payout reads "40.71" while the operator's wallet
  // is credited 40.71978 — which operators report as a discrepancy. See
  // displayExactMoney.ts. Fiat only; crypto is untouched.
  //
  // 🚨 This swaps the FORMATTER ONLY. It must never widen which statuses render a
  // number — the '-' branches below, especially the pending-crash guard, are the
  // whole reason a player is not told they won the liability ceiling.
  exact = false
) => {
  const amount = exact
    ? displayExactMoney(currentBet.potentialPayout, currentBet.currency)
    : displayMoney(currentBet.potentialPayout.toString(), true)

  const BetAmountMap: Record<BetStatus, string> = {
    [betStatusMap.WON]: amount,
    [betStatusMap.LOST]: '-',
    [betStatusMap.CANCELLED]: '-',
    [betStatusMap.PENDING]: isCrash ? '-' : amount,
    [betStatusMap.CASHED_OUT]: amount
  }

  return BetAmountMap[currentBetStatus]
}

export const namespaceKeys = {
  Win: 'Win',
  Bet: 'Bet',
  Cancel: 'Cancel',
  BetEvent: 'BetEvent'
} as const

export const namespaceKeyList = Object.values(namespaceKeys) as NonEmptyArray<
  (typeof namespaceKeys)[keyof typeof namespaceKeys]
>

// This is almost the same code as in common/src/utils/id.ts

interface AppError {
  type: 'AppError'
  subtype: string
  message: string
  context: Record<string, unknown>
}

const namespaceMap: Record<(typeof namespaceKeys)[keyof typeof namespaceKeys], string> = {
  Win: '6d718b64-d68a-4930-afb2-a3fa3f22bd5b',
  Bet: '9ccc439d-667a-45da-8a00-292995cfb75d',
  Cancel: '79dddad4-659b-4cf4-8648-df3cabf209b9',
  BetEvent: '4a0b99a7-53a6-4fb1-8578-93ddeb502715'
}

export type NamespaceKey = keyof typeof namespaceMap

export const generateIdByData = (
  data: unknown,
  namespaceKey: NamespaceKey
): Effect.Effect<string, AppError> => {
  if (!data) {
    return Effect.fail({
      type: 'AppError',
      subtype: 'GenerateIdByDataError',
      message: 'Failed to generate UUID, data is undefined',
      context: {
        data
      }
    })
  }

  const program = Effect.try({
    try: () => {
      const input = JSON.stringify(data)
      return uuidv5(input, namespaceMap[namespaceKey])
    },
    catch: (error) => {
      console.error(error)
      const appError: AppError = {
        type: 'AppError',
        subtype: 'GenerateIdByDataError',
        message: 'Failed to generate UUID',
        context: {
          data
        }
      }

      return appError
    }
  })

  return program
}

export const generateBetId = (bet: Omit<Bet, 'id'>): Effect.Effect<string, AppError> => {
  return generateIdByData(
    {
      gameId: bet.raceConfigurationId,
      roundId: bet.raceId,
      playerId: bet.playerId,
      amount: toInteger(bet.betAmount, bet.currency),
      currency: bet.currency,
      details: bet.betDetails
    },
    'Bet'
  )
}

export const getBetStatusByRaceSettled = (
  bet: Bet,
  raceSettledEvent: RaceSettledEvent
): BetStatus => {
  const mainMarblesByPosition = new Map(
    raceSettledEvent.payload.results.map((result): [number, string] => [
      Number(result.position),
      result.marbleId
    ])
  )

  const mainStatus = processBetStatus(bet, mainMarblesByPosition)

  // For chip-placement games with side-bet markets (Even/Odd,
  // Exact-Boundary, etc.), the main `results` array carries the
  // main-market winner only — a side-bet bet's selectedItem (e.g. the
  // Even marble) will not appear there. Without consulting
  // `marketResults`, every side-bet bet is wrongly marked LOST on
  // race_settled, which then triggers `actions.bet.betLost` and emits
  // `status: 'lost'` to the parent iframe before the bet:won broadcast
  // arrives to correct it. Side-bet markets only apply to PickWinner
  // bets — Forecast/Tricast aren't used in chip-placement.
  if (mainStatus === betStatusMap.WON || bet.betDetails.type !== 'PickWinner') {
    return mainStatus
  }

  const marketResults = raceSettledEvent.payload.marketResults
  if (!marketResults) {
    return mainStatus
  }

  for (const marketWinners of Object.values(marketResults)) {
    const sideMarblesByPosition = new Map(
      marketWinners.map((result): [number, string] => [Number(result.position), result.marbleId])
    )
    if (processBetStatus(bet, sideMarblesByPosition) === betStatusMap.WON) {
      return betStatusMap.WON
    }
  }

  return mainStatus
}

const processBetStatus = (bet: Bet, marblesByPosition: Map<number, string>): BetStatus => {
  const betSelectedItems = bet.betDetails.selectedItems
  const firstPlace = marblesByPosition.get(1)
  const secondPlace = marblesByPosition.get(2)
  const thirdPlace = marblesByPosition.get(3)

  switch (bet.betDetails.type) {
    case 'PickWinner': {
      if (!firstPlace) {
        console.error('Should not happen: no winning marble found')
        return betStatusMap.PENDING
      }

      const pickWinnerMatch = betSelectedItems.includes(firstPlace)

      return pickWinnerMatch ? betStatusMap.WON : betStatusMap.LOST
    }
    case 'StraightForecast': {
      // exact order
      if (!firstPlace || !secondPlace) {
        console.error('Should not happen: no first or second place found')
        return betStatusMap.PENDING
      }

      const straightForecastMatch =
        betSelectedItems[0] === firstPlace && betSelectedItems[1] === secondPlace

      return straightForecastMatch ? betStatusMap.WON : betStatusMap.LOST
    }
    case 'ReverseForecast': {
      // any order
      if (!firstPlace || !secondPlace) {
        console.error('Should not happen: no first or second place found')
        return betStatusMap.PENDING
      }

      const reverseForecastMatch = containsSameItems(betSelectedItems, [firstPlace, secondPlace])

      return reverseForecastMatch ? betStatusMap.WON : betStatusMap.LOST
    }
    case 'Tricast': {
      // exact order
      if (!firstPlace || !secondPlace || !thirdPlace) {
        console.error('Should not happen: no first, second or third place found.')
        return betStatusMap.PENDING
      }

      const tricastMatch =
        betSelectedItems[0] === firstPlace &&
        betSelectedItems[1] === secondPlace &&
        betSelectedItems[2] === thirdPlace

      return tricastMatch ? betStatusMap.WON : betStatusMap.LOST
    }
    case 'CombinationTricast': {
      // any order
      if (!firstPlace || !secondPlace || !thirdPlace) {
        console.error('Should not happen: no first, second or third place found')
        return betStatusMap.PENDING
      }

      const combinationTricastMatch = containsSameItems(betSelectedItems, [
        firstPlace,
        secondPlace,
        thirdPlace
      ])

      return combinationTricastMatch ? betStatusMap.WON : betStatusMap.LOST
    }
    default:
      console.error('Should not happen: unknown bet type')
      return betStatusMap.PENDING
  }
}
